import {
    HttpErrorResponse,
} from '@angular/common/http';

import {
    Injectable,
    NgZone,
    inject,
} from '@angular/core';

import {
    Router,
} from '@angular/router';

import {
    API_V1_URL,
} from '../config/api.config';

import {
    AuthService,
} from './auth.service';

import {
    ToastService,
} from './toast.service';

interface SessionInvalidatedPayload {
    reason?: string;
}

interface BackendProblemDetails {
    code?: string;
    title?: string;
    detail?: string;
}

const SESSION_REVOKED_CODE =
    'auth.session_revoked';

const SESSION_REVOCATION_UNAVAILABLE_CODE =
    'auth.session_revocation_unavailable';

@Injectable({
    providedIn:
        'root',
})
export class SessionEventsService {
    private readonly authService =
        inject(AuthService);

    private readonly router =
        inject(Router);

    private readonly toastService =
        inject(ToastService);

    private readonly zone =
        inject(NgZone);

    private abortController:
        AbortController |
        null = null;

    private isStarted =
        false;

    private reconnectTimer:
        number |
        null = null;

    private hasShownRevocationUnavailable =
        false;

    start(): void {
        if (
            this.isStarted ||
            !this.authService.getToken()
        ) {
            return;
        }

        this.isStarted =
            true;

        this.connect();
    }

    stop(): void {
        this.isStarted =
            false;

        this.hasShownRevocationUnavailable =
            false;

        if (
            this.reconnectTimer
        ) {
            window.clearTimeout(
                this.reconnectTimer
            );

            this.reconnectTimer =
                null;
        }

        this.abortController
            ?.abort();

        this.abortController =
            null;
    }

    private async connect():
        Promise<void> {
        this.abortController
            ?.abort();

        this.abortController =
            new AbortController();

        const token =
            this.authService
                .getToken();

        if (!token) {
            this.handleLogoutEvent();
            return;
        }

        try {
            const response =
                await fetch(
                    `${API_V1_URL}/events/session`,
                    {
                        method:
                            'GET',

                        headers: {
                            Accept:
                                'text/event-stream',

                            Authorization:
                                `Bearer ${token}`,
                        },

                        signal:
                            this.abortController
                                .signal,
                    }
                );

            /*
             * SSE uses fetch directly, therefore Angular
             * interceptors do not see these responses.
             *
             * FV-267 must therefore also be handled here.
             */
            if (
                !response.ok
            ) {
                const problem =
                    await this.readProblemDetails(
                        response
                    );

                /*
                 * Temporary Redis/session-revocation outage.
                 *
                 * Keep session + token.
                 * Do not redirect.
                 * Reconnect automatically.
                 */
                if (
                    response.status ===
                    503 &&
                    problem?.code ===
                    SESSION_REVOCATION_UNAVAILABLE_CODE
                ) {
                    this.handleSessionRevocationUnavailable(
                        problem
                    );

                    this.scheduleReconnect();

                    return;
                }

                /*
                 * Definitively revoked session.
                 */
                if (
                    response.status ===
                    401 &&
                    problem?.code ===
                    SESSION_REVOKED_CODE
                ) {
                    this.handleLogoutEvent(
                        problem.detail
                    );

                    return;
                }

                /*
                 * Keep the existing behaviour for every
                 * other 401/403 response.
                 */
                if (
                    response.status ===
                    401 ||
                    response.status ===
                    403
                ) {
                    this.handleLogoutEvent();

                    return;
                }

                /*
                 * Existing behaviour for other temporary
                 * SSE failures.
                 */
                this.scheduleReconnect();

                return;
            }

            /*
             * Revocation service is reachable again.
             */
            this.hasShownRevocationUnavailable =
                false;

            if (
                !response.body
            ) {
                this.scheduleReconnect();

                return;
            }

            await this.readStream(
                response.body
            );
        } catch {
            if (
                this.isStarted
            ) {
                this.scheduleReconnect();
            }
        }
    }

    private async readStream(
        body:
            ReadableStream<Uint8Array>
    ): Promise<void> {
        const reader =
            body.getReader();

        const decoder =
            new TextDecoder();

        let buffer =
            '';

        while (
            this.isStarted
        ) {
            const {
                value,
                done,
            } =
                await reader.read();

            if (done) {
                break;
            }

            buffer +=
                decoder.decode(
                    value,
                    {
                        stream:
                            true,
                    }
                );

            const events =
                buffer.split(
                    '\n\n'
                );

            buffer =
                events.pop() ??
                '';

            for (
                const rawEvent of
                events
            ) {
                this.handleRawSseEvent(
                    rawEvent
                );
            }
        }

        if (
            this.isStarted
        ) {
            this.scheduleReconnect();
        }
    }

    private handleRawSseEvent(
        rawEvent: string
    ): void {
        const eventName =
            this.getSseField(
                rawEvent,
                'event'
            );

        const data =
            this.getSseField(
                rawEvent,
                'data'
            );

        if (
            !eventName ||
            eventName ===
            'connected'
        ) {
            return;
        }

        if (
            eventName ===
            'session.invalidated'
        ) {
            const reason =
                this.readReasonFromData(
                    data
                );

            this.handleSessionInvalidated(
                reason
            );

            return;
        }

        this.handleSessionInvalidated(
            eventName
        );
    }

    private handleSessionInvalidated(
        reason?: string
    ): void {
        this.zone.run(
            () => {
                switch (
                reason
                ) {
                    case 'roles_changed':
                    case 'modules_changed':
                    case 'filialen_changed':
                        this.refreshPermissionsAfterEvent();
                        break;

                    case 'deactivated':
                    case 'force_logout':
                    case 'deleted':
                    case 'user_deleted':
                        this.handleLogoutEvent();
                        break;
                }
            }
        );
    }

    private refreshPermissionsAfterEvent():
        void {
        this.authService
            .hydrateRuntimePermissions()
            .subscribe({
                next:
                    () => {
                        const currentUrl =
                            this.router.url;

                        const nextRoute =
                            this.authService
                                .getFirstAllowedRoute();

                        const isStillAllowed =
                            currentUrl.startsWith(
                                nextRoute
                            ) ||
                            (
                                currentUrl.startsWith(
                                    '/benutzerverwaltung'
                                ) &&
                                this.authService
                                    .canSeeBenutzerverwaltung()
                            );

                        if (
                            !isStillAllowed
                        ) {
                            this.router.navigate([
                                nextRoute,
                            ]);
                        }
                    },

                error:
                    (
                        error:
                            unknown
                    ) => {
                        /*
                         * These HttpClient calls already pass
                         * through authInterceptor.
                         *
                         * Do not turn FV-267's temporary 503
                         * into a logout here afterwards.
                         *
                         * Also avoid duplicate logout handling
                         * for auth.session_revoked.
                         */
                        if (
                            this.isSessionRevocationUnavailableError(
                                error
                            ) ||
                            this.isSessionRevokedError(
                                error
                            )
                        ) {
                            return;
                        }

                        /*
                         * Existing behaviour for all other
                         * errors remains unchanged.
                         */
                        this.handleLogoutEvent();
                    },
            });
    }

    private handleSessionRevocationUnavailable(
        problem:
            BackendProblemDetails
    ): void {
        /*
         * Reconnect happens every 3 seconds while SSE
         * is unavailable. Show the outage message only
         * once until a successful connection is restored.
         */
        if (
            this.hasShownRevocationUnavailable
        ) {
            return;
        }

        this.hasShownRevocationUnavailable =
            true;

        this.zone.run(
            () => {
                this.toastService.warning(
                    problem.title
                        ?.trim() ||
                    'Dienst nicht verfügbar',

                    problem.detail
                        ?.trim() ||
                    'Der Dienst ist vorübergehend nicht verfügbar. Bitte versuchen Sie es in Kürze erneut.',

                    8000
                );
            }
        );
    }

    private handleLogoutEvent(
        message?: string
    ): void {
        this.stop();

        this.authService
            .clearSession();

        this.toastService.warning(
            'Sitzung beendet',

            message?.trim() ||
            'Bitte melden Sie sich erneut an.'
        );

        this.router.navigate([
            '/login',
        ]);
    }

    private scheduleReconnect():
        void {
        if (
            !this.isStarted ||
            this.reconnectTimer
        ) {
            return;
        }

        this.reconnectTimer =
            window.setTimeout(
                () => {
                    this.reconnectTimer =
                        null;

                    this.connect();
                },

                3000
            );
    }

    private isSessionRevocationUnavailableError(
        error: unknown
    ): boolean {
        return (
            error instanceof
            HttpErrorResponse &&
            error.status ===
            503 &&
            this.getProblemCode(
                error
            ) ===
            SESSION_REVOCATION_UNAVAILABLE_CODE
        );
    }

    private isSessionRevokedError(
        error: unknown
    ): boolean {
        return (
            error instanceof
            HttpErrorResponse &&
            error.status ===
            401 &&
            this.getProblemCode(
                error
            ) ===
            SESSION_REVOKED_CODE
        );
    }

    private getProblemCode(
        error:
            HttpErrorResponse
    ): string | null {
        const value =
            error.error;

        if (
            typeof value !==
            'object' ||
            value === null
        ) {
            return null;
        }

        const code =
            (
                value as
                BackendProblemDetails
            ).code;

        return (
            typeof code ===
                'string'
                ? code
                : null
        );
    }

    private async readProblemDetails(
        response: Response
    ): Promise<
        BackendProblemDetails |
        null
    > {
        try {
            const value =
                (
                    await response.json()
                ) as unknown;

            if (
                typeof value !==
                'object' ||
                value === null
            ) {
                return null;
            }

            return value as BackendProblemDetails;
        } catch {
            return null;
        }
    }

    private readReasonFromData(
        data:
            string |
            null
    ): string | undefined {
        if (!data) {
            return undefined;
        }

        try {
            const payload =
                JSON.parse(
                    data
                ) as SessionInvalidatedPayload;

            return payload.reason;
        } catch {
            return undefined;
        }
    }

    private getSseField(
        rawEvent: string,
        fieldName: string
    ): string | null {
        const prefix =
            `${fieldName}:`;

        const line =
            rawEvent
                .split(
                    '\n'
                )
                .find(
                    (item) =>
                        item
                            .trimStart()
                            .startsWith(
                                prefix
                            )
                );

        if (!line) {
            return null;
        }

        return line
            .slice(
                line.indexOf(
                    ':'
                ) +
                1
            )
            .trim();
    }
}